import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TagsLanguageService } from '../../features/tags-language/tagsLanguage.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NoDataComponent } from '../../shared/components/no-data/nodata.component';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { MatDialog } from '@angular/material/dialog';
import { BadgesService } from '../../features/badges/badges.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { PermissionDirective } from '../../shared/components/directives/permissions.directives';
import { ContentLoaderComponent } from '../../shared/components/content-loader-component/contentLoader.component';

@Component({
  selector: 'app-badges',
  imports: [
    CommonModule,
    NoDataComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    PermissionDirective,
    ContentLoaderComponent,
  ],
  templateUrl: './badges.component.html',
  styleUrl: './badges.component.scss',
})
export class BadgesComponent {
  readonly dialog = inject(MatDialog);
  public badgeList: any[] = [];
  public languages: any[] = [];
  public langCode: String = '';
  private searchSubject = new Subject<string>();
  noData = false;
  loading = false;
  searchTerm: string = '';

  constructor(
    private router: Router,
    private tagsService: TagsLanguageService,
    private toastr: ToastrService,
    private badgesService: BadgesService
  ) {}

  ngOnInit() {
    this.getAllBadges(this.langCode);
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAllBadges(this.langCode, searchTerm);
      });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  getLanguages(): void {
    this.tagsService.getLangauges().subscribe({
      next: (res) => {
        if (res?.data) {
          this.languages = res?.data;
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/images/placeHolderImage.jpg';
  }

  truncateTitle(title: string, maxLength: number = 10): string {
  if (!title) return '';
  if (title.length <= maxLength) return title;
  return title.substring(0, maxLength) + '...';
}

  getAllBadges(lang: any, search = ''): void {
    this.loading = true;
    this.badgesService.getAllBadges(lang, search).subscribe({
      next: (res) => {
        this.badgeList = res?.data;
        if (this.badgeList.length === 0) {
          this.noData = true;
        } else {
          this.noData = false;
        }
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  getTranslationName(badge: any): string {
    if (!badge?.badgeTranslations?.length) return '';
    const preferred = badge.badgeTranslations.find((t: any) => t.langId === 1);
    return (preferred?.title || badge.badgeTranslations[0].title).slice(0, 30);
  }

  onLanguageSelect(event: any) {
    this.langCode = event.value;
    this.getAllBadges(this.langCode);
  }

  onAddBadge() {
    this.router.navigate(['badge/new']);
  }

  onDeleteBadge(id: any): void {
    const dialogRef = this.dialog.open(ConfirmDialog);

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.badgesService.deleteBadge(id).subscribe({
          next: (res) => {
            this.toastr.success(res?.message);
            this.getAllBadges(this.langCode);
          },
          error: (err) => {
            console.log(err);
          },
        });
      }
    });
  }

  onEditBadge(id: any) {
    this.router.navigate([`badge/edit/${id}`]);
  }
}
